import type { FC } from 'react';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { isTransportActive } from 'src/components/layout/app-shell/is-transport-active';
import { NewDesignToggle } from 'src/components/layout/app-shell/new-design-toggle';
import { useAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { HeaderPrimary } from 'src/components/layout/header/primary';
import { HeaderSecondary } from 'src/components/layout/header/secondary';
import { isDashboardActive } from 'src/components/layout/header/secondary-nav-dashboard';
import { isGdmActive } from 'src/components/layout/header/secondary-nav-gdm';
import { isH2Active } from 'src/components/layout/header/secondary-nav-h2';
import { isLngActive } from 'src/components/layout/header/secondary-nav-lng';
import { isMarketMessagesActive } from 'src/components/layout/header/secondary-nav-market-messages';
import { isReportingActive } from 'src/components/layout/header/secondary-nav-reporting';
import { isStorageActive } from 'src/components/layout/header/secondary-nav-storage';
import { useMobileNav } from 'src/components/layout/header/use-mobile-nav';
import { LayoutHeader } from 'src/components/layout/layout';
import { SimpleHeader } from 'src/components/layout/simple-page-layout';
import { AngularModalDimmer } from 'src/components/overlay/angular-modal-dimmer';
import { stickyHeaderId } from 'src/components/scroll-management';
import { useHeaderHeightMeasure } from 'src/hooks/use-header-height';

export const Header: FC = () => {
  const { pathname, hash } = useLocation();

  const appShellData = useAppShellData();

  const activeSection = useMemo(() => {
    const dashboard = isDashboardActive(pathname, hash);
    const transport = isTransportActive(pathname, hash);
    const storage = isStorageActive(pathname, hash);
    const lng = isLngActive(pathname);
    const h2 = isH2Active(pathname);
    const reporting = isReportingActive(pathname, hash);
    const gdm = isGdmActive(pathname);
    const marketMessages = isMarketMessagesActive(pathname);
    return {
      dashboard,
      transport,
      storage,
      reporting,
      gdm,
      lng,
      h2,
      marketMessages,
    };
  }, [pathname, hash]);

  const ref = useHeaderHeightMeasure();
  const mobileNav = useMobileNav();

  if (!appShellData)
    return (
      <LayoutHeader id={stickyHeaderId} ref={ref}>
        <SimpleHeader />
      </LayoutHeader>
    );

  return (
    <LayoutHeader
      id={stickyHeaderId}
      ref={ref}
      fullscreen={mobileNav.value !== null}
      onClick={(e) => {
        if (mobileNav.value === null) return;
        // when the mobile nav is open the LayoutHeader is covering the whole screen,
        // even if it is invisble. when the user directly clicks on the LayoutHeader (which means the
        // user actually wanted to click _outside_ the mobile nav) we close the mobile nav.
        if (e.target instanceof HTMLElement && e.target.id === stickyHeaderId)
          mobileNav.setValue(null);
      }}
    >
      <AngularModalDimmer>
        <NewDesignToggle />

        <header>
          <HeaderPrimary
            activeSection={activeSection}
            authorizationService={appShellData.authorizationService}
            organisationService={appShellData.organisationService}
            capacityManagementService={appShellData.capacityManagementService}
            regularFcfsService={appShellData.regularFcfsService}
            directFcfsService={appShellData.directFcfsService}
            hydrogenFcfsRoot={appShellData.hydrogenFcfsService}
            assignmentService={appShellData.assignmentService}
            remitReportingService={appShellData.remitReportingService}
            mobileNav={mobileNav}
            mapPrototype={appShellData.mapPrototype}
          />
          <HeaderSecondary
            activeSection={activeSection}
            mobileNav={mobileNav}
            appShellData={appShellData}
          />
        </header>
        <div
          id="react-loading-bar"
          className="fixed h-px w-[0%] bg-legacy-brand opacity-100"
        />
      </AngularModalDimmer>
    </LayoutHeader>
  );
};
